import type { DateRange, EventData } from '../types';
import { addDays, addHours, addMinutes, format, startOfDay } from 'date-fns';

// Backend days are UTC and every format it sends starts with that date; parsing
// one instead rolls it back a day for viewers west of UTC.
const backendDayKey = (date: string | Date | number) =>
  typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)
    ? date.slice(0, 10)
    : new Date(date).toISOString().slice(0, 10);

const dailyChartData = (data: EventData[], dateRange: DateRange) => {
  const counts = new Map<string, number>();
  for (const row of data ?? []) {
    if (!row.date) continue;
    const key = backendDayKey(row.date);
    if (!counts.has(key)) counts.set(key, +row.count);
  }

  // Steps whole local days so a DST change can't drop the last one.
  const days = [];
  const last = startOfDay(dateRange.endDate);
  for (
    let day = startOfDay(dateRange.startDate);
    day <= last;
    day = addDays(day, 1)
  ) {
    const key = format(day, 'yyyy-MM-dd');
    days.push({
      name: day.toLocaleDateString(),
      time: day.getTime(),
      groupKey: key,
      count: counts.get(key) ?? 0,
    });
  }
  return days;
};

function getXAxis(startDate: Date, stopDate: Date, interval: string) {
  const dateArray = [];
  let currentDate = startDate;
  while (currentDate <= stopDate) {
    dateArray.push(new Date(currentDate));
    if (interval === '5 minutes') {
      currentDate = addMinutes(currentDate, 5);
    } else if (interval === '1 days') {
      currentDate = addDays(currentDate, 1);
    } else if (interval === '1 hour') {
      currentDate = addHours(currentDate, 1);
    } else {
      currentDate = addDays(currentDate, 1);
    }
  }
  return dateArray;
}

const formatGroupKey = (date: Date | string | number, interval: string) => {
  if (interval === '5 minutes' || interval === '1 hour') {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (interval === '1 days') {
    return new Date(date).toLocaleDateString([], {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  return date;
};

const formatDate = (date: Date | string | number, interval: string) => {
  if (interval === '5 minutes' || interval === '1 hour') {
    return new Date(date).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } else if (interval === '1 days') {
    return new Date(date).toLocaleDateString();
  }

  return date;
};

export const calculateChartData = (data: EventData[], dateRange: DateRange) => {
  const interval = dateRange.interval;
  if (interval === '1 days') {
    return dailyChartData(data, dateRange);
  }
  const calculated = getXAxis(
    dateRange.startDate,
    dateRange.endDate,
    interval,
  ).map((date) => ({
    name: formatDate(date, interval),
    time: date.getTime(),
    groupKey: formatGroupKey(date, interval),
    count: 0,
  }));

  if (!data) {
    return calculated;
  }

  const counts = data.map((date) => {
    if (!date.date) {
      return date;
    }
    return {
      name: formatDate(date.date, interval),
      time: new Date(date.date).getTime(),
      groupKey: formatGroupKey(date.date, interval),
      count: +date.count,
    };
  });

  const mappedDates = calculated.map((date) => {
    const count = counts.find(
      (countDate) => countDate.groupKey === date.groupKey,
    );
    if (count) {
      return count;
    }

    return date;
  });

  return mappedDates;
};
